/**
 * Minimal shared MODAL primitive for the studio's full-surface shared
 * components (image editor, camera-angle adjuster). Centered dialog over
 * a scrim; Escape + scrim-click close. Not for the slide-in entity
 * drawer (that's EntityEditDrawer).
 *
 * FROZEN SHARED COMPONENT — owned by the foundation-integrator.
 */

import "./shared.css";

export interface ModalHandle {
  /** The scrollable content area to render into. */
  body: HTMLElement;
  /** Programmatically close + tear down the modal. */
  close: () => void;
  /** The outer root element. */
  root: HTMLElement;
}

export interface ModalOptions {
  title: string;
  /** Extra class on the dialog for size/skin variants. */
  variant?: string;
  /** Fired once the modal is fully removed. */
  onClose?: () => void;
  /** Mount target (defaults to document.body). */
  host?: HTMLElement;
  /** Tag for koko/test targeting on the dialog. */
  surface?: string;
}

/** Open a centered modal and return a handle to its body + close fn. */
export function openModal(options: ModalOptions): ModalHandle {
  const host = options.host ?? document.body;

  const root = document.createElement("div");
  root.className = "studio-modal-root";
  if (options.surface) root.dataset.kokoSurface = options.surface;

  const scrim = document.createElement("div");
  scrim.className = "studio-modal-scrim";

  const dialog = document.createElement("div");
  dialog.className = "studio-modal-dialog";
  if (options.variant) dialog.classList.add(options.variant);
  dialog.setAttribute("role", "dialog");
  dialog.setAttribute("aria-modal", "true");

  const header = document.createElement("div");
  header.className = "studio-modal-header";
  const titleEl = document.createElement("h3");
  titleEl.className = "studio-modal-title";
  titleEl.textContent = options.title;
  const closeButton = document.createElement("button");
  closeButton.type = "button";
  closeButton.className = "studio-modal-close";
  closeButton.setAttribute("aria-label", "Close");
  closeButton.textContent = "×";
  header.appendChild(titleEl);
  header.appendChild(closeButton);

  const body = document.createElement("div");
  body.className = "studio-modal-body";

  dialog.appendChild(header);
  dialog.appendChild(body);
  root.appendChild(scrim);
  root.appendChild(dialog);
  host.appendChild(root);

  let closed = false;
  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === "Escape") {
      event.stopPropagation();
      close();
    }
  };

  const close = (): void => {
    if (closed) return;
    closed = true;
    document.removeEventListener("keydown", onKeyDown, true);
    root.dataset.open = "false";
    window.setTimeout(() => {
      root.remove();
      options.onClose?.();
    }, 200);
  };

  scrim.addEventListener("click", close);
  closeButton.addEventListener("click", close);
  document.addEventListener("keydown", onKeyDown, true);

  // Two-frame entry for the fade/scale-in.
  root.dataset.open = "false";
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      root.dataset.open = "true";
    });
  });

  return { body, close, root };
}
